const MAX_XERROR_NAME_LENGTH = 80

export type XErrorCategory = "something"

export type XErrorParams = {
    orgId?: string | null
    message: string
    code?: string
    category?: XErrorCategory
    data?: any
    cause?: Error | Error[] | null
    sentryTags?: Record<string, string>
    runId?: string
    statusCode?: number
    fingerprint?: string[]
}

type ErrorDetails = {
    name: string
    message: string
    data?: unknown
    stack?: string
}

type ErrorData = {
    [key: string]: ErrorDetails
}

export class XError extends Error {
    orgId?: string | null
    message: string
    code?: string
    category?: XErrorCategory
    data?: any
    cause: Error | Error[] | null
    sentryTags?: Record<string, string>
    runId?: string
    statusCode?: number
    fingerprint?: string[]

    customFingerPrint: string

    constructor({
        orgId,
        code,
        cause = null,
        message,
        category,
        data,
        sentryTags,
        runId,
        statusCode,
        fingerprint,
    }: XErrorParams) {
        super(message)
        const derivedCode = code || getFirstLetters(message)

        this.name =
            code ||
            message.slice(0, MAX_XERROR_NAME_LENGTH) ||
            this.constructor.name
        this.message = message
        this.orgId = orgId
        this.code = code
        this.category = category
        this.data = data
        this.sentryTags = sentryTags
        this.runId = runId
        this.statusCode = statusCode
        this.fingerprint = fingerprint

        if (Array.isArray(cause) || cause instanceof Error || cause === null) {
            this.cause = cause
        } else {
            const typedError = cause as Error
            this.cause = typedError
        }

        this.customFingerPrint = derivedCode

        Error.captureStackTrace?.(this, XError)
    }
}

export const xerr = (params: XErrorParams): XError => {
    return new XError(params)
}

export const toError = (cause: unknown): Error =>
    cause instanceof Error ? cause : new XError({ message: String(cause) })

export const withCatchAllWorkflowErrors =
    <P, R>(
        onError: (params: P) => Omit<XErrorParams, "cause">,
        fn: (params: P) => Promise<R>
    ) =>
    async (params: P): Promise<R> => {
        try {
            return await fn(params)
        } catch (error) {
            throw new XError({ ...onError(params), cause: error as Error })
        }
    }

export const getAllErrors = (error: Error): Error[] => {
    const errors = [error]

    if (!(error instanceof XError)) return errors

    if (Array.isArray(error.cause)) {
        error.cause.forEach(cause => {
            errors.push(...getAllErrors(cause))
        })
    } else if (error.cause) {
        errors.push(...getAllErrors(error.cause))
    }

    return errors
}

export const getAggregatedMessage = (errors: Error[]): string => {
    return errors
        .map(error => error.message ?? error.name ?? JSON.stringify(error))
        .join(" -> ")
}

export const describeErrorChain = (err: unknown): string =>
    err instanceof Error ? getAggregatedMessage(getAllErrors(err)) : String(err)

export const getCustomFingerPrint = (errors: Error[]): string => {
    return errors.map(error => error.name).join("-")
}

export const getErrorData = (allErrors: Error[]): ErrorData => {
    const result: ErrorData = {}

    for (let i = 0, len = allErrors.length; i < len; i++) {
        const error = allErrors[i]

        const errorData: ErrorDetails =
            error instanceof XError
                ? {
                      name: error.name,
                      data: error.data,
                      message: error.message,
                      stack: error.stack,
                  }
                : error

        result[`${i + 1}: ${error.message}`] = errorData
    }

    return result
}

export const getFirstLetters = (inputString: string) => {
    return inputString
        .split(" ")
        .filter(Boolean)
        .map(word => word[0].toLowerCase())
        .join("")
}

const ERROR_CAUSE_MAX_DEPTH = 10

export const errorHasCode = (err: unknown, code: string): boolean => {
    let cursor: unknown = err
    for (let depth = 0; depth < ERROR_CAUSE_MAX_DEPTH; depth++) {
        if (!cursor || typeof cursor !== "object") return false
        const candidate = cursor as { code?: unknown; cause?: unknown }
        if (candidate.code === code) return true
        cursor = candidate.cause
    }
    return false
}

export const firstErrorCode = (err: unknown): string | undefined => {
    let cursor: unknown = err
    for (let depth = 0; depth < ERROR_CAUSE_MAX_DEPTH; depth++) {
        if (!cursor || typeof cursor !== "object") return undefined
        const candidate = cursor as { code?: unknown; cause?: unknown }
        if (typeof candidate.code === "string") return candidate.code
        cursor = candidate.cause
    }
    return undefined
}

export const errorHasStatus = (err: unknown, status: number): boolean => {
    let cursor: unknown = err
    for (let depth = 0; depth < ERROR_CAUSE_MAX_DEPTH; depth++) {
        if (!cursor || typeof cursor !== "object") return false
        const candidate = cursor as { status?: unknown; cause?: unknown }
        if (candidate.status === status) return true
        cursor = candidate.cause
    }
    return false
}

export const errorHasStatusCode = (
    err: unknown,
    statusCode: number
): boolean => {
    let cursor: unknown = err
    for (let depth = 0; depth < ERROR_CAUSE_MAX_DEPTH; depth++) {
        if (!cursor || typeof cursor !== "object") return false
        const candidate = cursor as { statusCode?: unknown; cause?: unknown }
        if (candidate.statusCode === statusCode) return true
        cursor = candidate.cause
    }
    return false
}

const isAbortMessage = (error: Error): boolean =>
    error.name === "AbortError" ||
    error.message.toLowerCase().includes("aborted")

export const isAbortError = (error: unknown): boolean => {
    const seen = new Set<unknown>()

    const visit = (candidate: unknown, depth: number): boolean => {
        if (depth >= ERROR_CAUSE_MAX_DEPTH) return false
        if (candidate === null || typeof candidate !== "object") return false
        if (seen.has(candidate)) return false
        seen.add(candidate)

        if (candidate instanceof Error && isAbortMessage(candidate)) {
            return true
        }

        const cause = (candidate as { cause?: unknown }).cause
        if (Array.isArray(cause)) {
            return cause.some(child => visit(child, depth + 1))
        }
        return visit(cause, depth + 1)
    }

    return visit(error, 0)
}
